import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent } from '@mui/material';
import { useMyContext } from '../../../context/MyContext';
import { useNavigate } from 'react-router-dom';
import Spinner from '../../../component/spinner/spinner';
import axios from 'axios';
import { toast } from 'react-toastify';
import '../../css/custom-scrollbar.css';
import { Base_Url, createShippment } from '../../../config/config';
import {
  Truck,
  Zap,
  Check,
  X,
  Sparkles,
  ArrowRight,
  ShieldCheck,
  ArrowUpDown,
  Layers
} from 'lucide-react';

const ViewRateForword = ({ isopen, onClose, data, type }) => {
  const { isDarkMode } = useMyContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [modeFilter, setModeFilter] = useState('all'); // 'all' | 'surface' | 'express'
  const [sortBy, setSortBy] = useState('cheapest'); // 'cheapest' | 'default'

  const handleNavigation = () => {
    navigate('/shipments/all');
  };

  const rawTableData = useMemo(() => {
    if (!data?.data?.partners_pricing) return [];

    const allPartners = Object.keys(data.data.partners_pricing);

    return allPartners.flatMap((partner) =>
      Object.entries(data.data.partners_pricing[partner] || {}).map(([mode, pricing], index) => {
        const codTotal = Number(pricing.cod_total_price);
        const base = Number(pricing.total_price);
        const finalPrice = !isNaN(codTotal) && codTotal !== 0 ? codTotal : base;

        return {
          id: `${partner}-${mode}-${index}`,
          courier: partner.toLowerCase(),
          mode,
          zone: data.data.zone,
          basePrice: pricing.total_price,
          codExtra: pricing.cod_extra,
          codTotalPrice: finalPrice,
          shippingMode: mode,
          ...pricing,
        };
      })
    );
  }, [data]);

  const minPrice = useMemo(() => {
    if (rawTableData.length === 0) return 0;
    return Math.min(...rawTableData.map((r) => Number(r.codTotalPrice || r.basePrice || 999999)));
  }, [rawTableData]);

  // Filtered & Sorted options
  const tableData = useMemo(() => {
    let list = [...rawTableData];

    // Filter by mode
    if (modeFilter === 'surface') {
      list = list.filter((r) => String(r.mode).toLowerCase().includes('surface'));
    } else if (modeFilter === 'express') {
      list = list.filter((r) => String(r.mode).toLowerCase().includes('express'));
    }

    // Sort
    if (sortBy === 'cheapest') {
      list.sort((a, b) => Number(a.codTotalPrice || a.basePrice || 0) - Number(b.codTotalPrice || b.basePrice || 0));
    }

    return list;
  }, [rawTableData, modeFilter, sortBy]);

  const handleSelectCard = (row) => {
    setSelectedRowId(row.id);
    setSelectedRowData(row);
  };

  const handleCreateShipment = async () => {
    const token = localStorage.getItem('token');
    const cusid = localStorage.getItem('cus_id');

    if (selectedRowData) {
      let requestBody;

      if (data.bulk_order === true) {
        requestBody = {
          order_id: data.selected_orders,
          shipping_mode: selectedRowData.shippingMode,
          partner: selectedRowData.courier,
        };
      } else {
        requestBody = {
          order_id: data.order_id,
          partner: selectedRowData.courier,
          shipping_mode: selectedRowData.shippingMode,
        };
      }

      setIsSubmitting(true);
      try {
        const response = await axios.post(Base_Url + createShippment, requestBody, {
          headers: {
            token,
            cus_id: cusid,
            'Content-Type': 'application/json',
          },
        });

        if (response.data.status === 'SUCCESS') {
          const { summary, failed_orders } = response.data;

          if (summary.failed === 0) {
            toast.success(`🎉 ${summary.success} shipment${summary.success > 1 ? 's' : ''} created successfully!`);
            handleClose();
            handleNavigation();
          } else if (summary.success === 0) {
            const errorMessages = failed_orders
              .map((order) => `❌ Order ${order.order_id}: ${order.message || 'Unknown error'}`)
              .join('\n');
            toast.error(`Failed to create ${summary.failed} shipment${summary.failed > 1 ? 's' : ''}.\n${errorMessages}`);
          } else {
            toast.info(`📊 ${summary.success} successful, ${summary.failed} failed out of ${summary.total} total shipments`);
            failed_orders.forEach((order) => {
              const remarks =
                order?.response?.packages?.[0]?.remarks?.join(', ') ||
                order.message ||
                'Unknown error';

              toast.error(`❌ Order ${order.order_id}: ${remarks}`);
            });
          }
        } else {
          const errorMessage = Array.isArray(response.data.message)
            ? response.data.message.join(', ')
            : response.data.message || 'An error occurred';
          toast.error(errorMessage);
        }
      } catch (error) {
        console.error('API Error:', error);
        toast.error(error.response?.data?.message || 'An error occurred');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleClose = () => {
    setSelectedRowId(null);
    setSelectedRowData(null);
    onClose();
  };

  const renderCourierLogo = (courier) => {
    const c = courier?.toLowerCase();
    if (c === 'delhivery') {
      return (
        <img
          src={isDarkMode ? '/assets/images/DELHIVERYWhite.png' : '/assets/images/Delhivery.png'}
          alt="Delhivery"
          className="h-5 w-auto object-contain max-w-[95px]"
        />
      );
    }
    if (c === 'ecom') {
      return (
        <img
          src={isDarkMode ? '/assets/images/logoecom.svg' : '/assets/images/ecom-express-logo.png'}
          alt="Ecom Express"
          className={`object-contain w-auto max-w-[95px] ${isDarkMode ? 'h-5' : 'h-6'}`}
        />
      );
    }
    if (c === 'ekart') {
      return <img src="/assets/images/ekart.png" alt="Ekart" className="h-5 w-auto object-contain max-w-[85px]" />;
    }
    if (c === 'xpressbees') {
      return <img src="/assets/images/xpressbess.png" alt="XpressBees" className="h-5 w-auto object-contain max-w-[95px]" />;
    }
    if (c === 'bluedart') {
      return <img src="/assets/images/bluedart.png" alt="bluedart" className="h-5 w-auto object-contain max-w-[85px]" />;
    }
    return (
      <span className="text-xs font-black uppercase tracking-wider text-slate-800 dark:text-white">
        {courier}
      </span>
    );
  };

  return (
    <Dialog
      open={isopen}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={window.innerWidth < 640}
      className={`custom-scrollbar ${isDarkMode ? 'dark:bg-darkmodeColor' : ''}`}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: isDarkMode ? 'rgba(15, 23, 42, 0.7)' : 'rgba(15, 23, 42, 0.45)',
          backdropFilter: 'blur(5px)',
        },
        '& .MuiDialog-paper': {
          width: { xs: '100%', sm: '92%', md: '86%' },
          maxWidth: { xs: '100%', sm: '980px' },
          height: { xs: '100%', sm: 'auto' },
          maxHeight: { xs: '100%', sm: '88vh' },
          margin: { xs: 0, sm: 'auto' },
          borderRadius: { xs: 0, sm: '28px' },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: isDarkMode ? '#0b1120' : '#ffffff',
          border: isDarkMode ? '1px solid rgba(99, 102, 241, 0.25)' : '1px solid rgba(226, 232, 240, 0.95)',
          boxShadow: isDarkMode
            ? '0 25px 60px -15px rgba(0, 0, 0, 0.7), 0 0 40px rgba(99, 102, 241, 0.15)'
            : '0 25px 60px -15px rgba(15, 23, 42, 0.12), 0 0 35px rgba(99, 102, 241, 0.08)',
        },
      }}
    >
      {/* 1. COMPACT MODERN LIGHT/DARK HEADER */}
      <div className={`relative px-5 sm:px-6 py-3 sm:py-3.5 flex-shrink-0 border-b overflow-hidden transition-colors ${isDarkMode
          ? 'bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 border-indigo-500/20 text-white'
          : 'bg-gradient-to-r from-slate-50 via-indigo-50/60 to-purple-50/50 border-slate-200/90 text-slate-900'
        }`}>
        {/* Subtle decorative glow spots */}
        <div className="absolute -top-16 -right-16 w-44 h-44 bg-indigo-500/10 dark:bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-44 h-44 bg-purple-500/10 dark:bg-purple-500/15 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-indigo-500 via-purple-600 to-violet-500 flex items-center justify-center flex-shrink-0 shadow-md shadow-indigo-500/20 ring-1.5 ring-indigo-200 dark:ring-white/20 text-white">
              <Truck size={17} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-widest font-bold text-indigo-600 dark:text-indigo-300">
                  Rate Calculator
                </span>
                {data?.order_id && (
                  <span className="px-2 py-0.5 rounded-full text-[9.5px] font-mono font-bold bg-white dark:bg-white/10 text-slate-700 dark:text-white/90 border border-slate-200/90 dark:border-white/10 shadow-2xs">
                    Order #{data.order_id}
                  </span>
                )}
                {data?.data?.zone && (
                  <span className="px-2 py-0.5 rounded-full text-[9.5px] font-bold bg-amber-50 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30">
                    Zone {data.data.zone}
                  </span>
                )}
              </div>
              <h2 className="text-sm sm:text-base font-extrabold text-slate-900 dark:text-white tracking-tight truncate mt-0.5">
                Compare & Choose Courier Partner
              </h2>
            </div>
          </div>

          <button
            type="button"
            onClick={handleClose}
            aria-label="Close dialog"
            className="w-8 h-8 rounded-full bg-white dark:bg-white/10 hover:bg-slate-100 dark:hover:bg-white/20 text-slate-500 dark:text-white/80 hover:text-slate-800 dark:hover:text-white border border-slate-200/90 dark:border-transparent flex items-center justify-center transition-all duration-200 hover:rotate-90 hover:scale-105 shadow-2xs shrink-0"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* 2. SUB-HEADER TOOLBAR (QUICK FILTERS & SORTING) */}
      <div className={`px-5 sm:px-6 py-2 border-b flex flex-wrap items-center justify-between gap-2 flex-shrink-0 transition-colors ${isDarkMode ? 'bg-slate-900/80 border-slate-800' : 'bg-slate-50/90 border-slate-200/80'
        }`}>
        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400">
          <Layers size={13} className="text-indigo-500" />
          <span>
            <strong className="text-slate-900 dark:text-white font-bold">{tableData.length}</strong> Options
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Mode Filters */}
          <div className={`p-0.5 rounded-lg border flex items-center gap-0.5 ${isDarkMode ? 'bg-slate-800/80 border-slate-700' : 'bg-white border-slate-200 shadow-xs'
            }`}>
            <button
              onClick={() => setModeFilter('all')}
              className={`px-2 py-0.5 rounded text-[10.5px] font-bold transition-all ${modeFilter === 'all'
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-white'
                }`}
            >
              All
            </button>
            <button
              onClick={() => setModeFilter('express')}
              className={`px-2 py-0.5 rounded text-[10.5px] font-bold transition-all flex items-center gap-1 ${modeFilter === 'express'
                  ? 'bg-amber-500 text-white shadow-xs'
                  : 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30'
                }`}
            >
              <Zap size={10} />
              Express
            </button>
            <button
              onClick={() => setModeFilter('surface')}
              className={`px-2 py-0.5 rounded text-[10.5px] font-bold transition-all flex items-center gap-1 ${modeFilter === 'surface'
                  ? 'bg-blue-600 text-white shadow-xs'
                  : 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30'
                }`}
            >
              <Truck size={10} />
              Surface
            </button>
          </div>

          {/* Sort By Lowest Rate */}
          <button
            onClick={() => setSortBy((prev) => (prev === 'cheapest' ? 'default' : 'cheapest'))}
            className={`px-2.5 py-1 rounded-lg border text-[10.5px] font-bold flex items-center gap-1 transition-all ${sortBy === 'cheapest'
                ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-700 dark:text-emerald-300'
                : isDarkMode
                  ? 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'
                  : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 shadow-xs'
              }`}
            title="Sort lowest price to highest"
          >
            <ArrowUpDown size={11} />
            <span>{sortBy === 'cheapest' ? 'Cheapest First' : 'Default'}</span>
          </button>
        </div>
      </div>

      {/* 3. COMPACT COURIER CARDS GRID */}
      <DialogContent
        className={`custom-scrollbar flex-1 px-4 sm:px-6 py-3.5 sm:py-4 overflow-y-auto ${isDarkMode ? 'bg-[#0b1120]' : 'bg-[#f8fafc]'
          }`}
        sx={{ overflowY: 'auto' }}
      >
        {tableData.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-3">
            {tableData.map((row) => {
              const isSelected = selectedRowId === row.id;
              const isCheapest = Number(row.codTotalPrice || row.basePrice || 0) === minPrice;
              const isExpress = String(row.mode).toLowerCase().includes('express');

              return (
                <div
                  key={row.id}
                  onClick={() => handleSelectCard(row)}
                  className={`group relative rounded-xl p-3 sm:p-3.5 border transition-all duration-200 cursor-pointer select-none flex flex-col justify-between ${isSelected
                      ? isDarkMode
                        ? 'border-indigo-500 bg-gradient-to-br from-indigo-950/50 to-slate-900 shadow-md shadow-indigo-500/20 ring-1.5 ring-indigo-500/40 -translate-y-0.5'
                        : 'border-indigo-600 bg-gradient-to-br from-indigo-50/70 to-white shadow-md shadow-indigo-500/15 ring-1.5 ring-indigo-500/30 -translate-y-0.5'
                      : isDarkMode
                        ? 'border-slate-800 bg-slate-900/70 hover:border-indigo-500/40 hover:bg-slate-900 hover:shadow-md'
                        : 'border-slate-200 bg-white hover:border-indigo-300 hover:shadow-md'
                    }`}
                >
                  {/* Selected Top Badge */}
                  {isSelected && (
                    <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-gradient-to-tr from-indigo-600 to-purple-600 text-white flex items-center justify-center shadow-md ring-2 ring-white dark:ring-slate-900">
                      <Check size={11} className="stroke-[3]" />
                    </div>
                  )}

                  {/* Top Row: Courier Logo + Mode Badge + Radio */}
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`h-7 px-2 rounded-lg border flex items-center justify-center transition-colors ${isDarkMode
                          ? 'bg-slate-800/80 border-slate-700'
                          : 'bg-white border-slate-200/90 shadow-2xs'
                        }`}>
                        {renderCourierLogo(row.courier)}
                      </div>

                      <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-[9.5px] font-bold ${isExpress
                          ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/25'
                          : 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/25'
                        }`}>
                        {isExpress ? <Zap size={9} /> : <Truck size={9} />}
                        <span className="capitalize">{row.mode || 'Standard'}</span>
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      {isCheapest && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-black bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30">
                          <Sparkles size={8} />
                          BEST
                        </span>
                      )}

                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${isSelected
                          ? 'border-indigo-600 bg-indigo-600 dark:border-indigo-500 dark:bg-indigo-500'
                          : 'border-slate-300 dark:border-slate-600 group-hover:border-indigo-400'
                        }`}>
                        {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white shadow-xs" />}
                      </div>
                    </div>
                  </div>

                  {/* Middle Row: Breakdown Details & Zone */}
                  <div className={`rounded-lg px-2.5 py-1.5 border mb-2 text-[10.5px] flex items-center justify-between transition-colors ${isSelected
                      ? isDarkMode
                        ? 'bg-slate-900/80 border-indigo-500/25'
                        : 'bg-white border-indigo-200/80'
                      : isDarkMode
                        ? 'bg-slate-800/40 border-slate-800'
                        : 'bg-slate-50 border-slate-200/60'
                    }`}>
                    <div className="text-slate-500 dark:text-slate-400 font-medium">
                      <span>Fwd: ₹{row.basePrice || 0}</span>
                      <span className="mx-1.5 opacity-40">•</span>
                      <span>COD: ₹{row.codExtra || 0}</span>
                    </div>

                    {row.zone && (
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${isDarkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-200/70 text-slate-600'
                        }`}>
                        Zone {row.zone}
                      </span>
                    )}
                  </div>

                  {/* Bottom Row: Total Price */}
                  <div className="flex items-baseline justify-between pt-1 border-t border-slate-100 dark:border-slate-800/80">
                    <div>
                      <span className="text-[9.5px] font-bold uppercase tracking-wider text-slate-400">
                        Total Rate
                      </span>
                      <span className="text-[8.5px] text-slate-400 block -mt-0.5">Incl. taxes</span>
                    </div>

                    <div className="text-right">
                      <span className={`text-base font-black tracking-tight font-mono ${isSelected
                          ? 'text-indigo-600 dark:text-indigo-400'
                          : 'text-slate-900 dark:text-white'
                        }`}>
                        ₹ {row.codTotalPrice || row.total_price}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-14 text-center">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3 shadow-inner">
              <Truck className="text-slate-400 dark:text-slate-500" size={24} />
            </div>
            <h3 className="text-xs font-bold text-slate-700 dark:text-slate-200">No courier options available</h3>
            <p className="text-[11px] text-slate-400 mt-1 max-w-sm">
              We couldn't find any shipping rates matching the selected filters. Please try switching mode.
            </p>
          </div>
        )}
      </DialogContent>

      {/* 4. ULTRA-PREMIUM STICKY FOOTER */}
      <div className={`flex-shrink-0 px-5 sm:px-7 py-3.5 sm:py-4 border-t flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3.5 transition-colors ${isDarkMode
          ? 'bg-slate-900/95 border-slate-800 backdrop-blur-md'
          : 'bg-white/95 border-slate-200/90 backdrop-blur-md'
        }`}>
        {/* Selected Courier Summary */}
        <div className="min-w-0">
          {selectedRowData?.id ? (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-indigo-500/15 to-purple-500/15 border border-indigo-500/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0">
                <ShieldCheck size={18} />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Selected Partner</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs sm:text-sm font-black text-slate-900 dark:text-white capitalize">
                    {selectedRowData.courier} · {selectedRowData.mode}
                  </span>
                  <span className="text-xs font-black text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-md border border-emerald-500/20 font-mono">
                    ₹ {selectedRowData.codTotalPrice || selectedRowData.total_price}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-xs text-slate-400 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              Click any courier card above to choose your preferred rate
            </p>
          )}
        </div>

        {/* Buttons */}
        <div className="flex items-center gap-2.5 justify-end">
          <button
            type="button"
            onClick={handleClose}
            className={`px-4 py-2.5 rounded-xl border text-xs font-bold transition-all ${isDarkMode
                ? 'bg-slate-800 hover:bg-slate-700 border-slate-700 text-slate-300'
                : 'bg-slate-100 hover:bg-slate-200 border-slate-200 text-slate-700'
              }`}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleCreateShipment}
            disabled={!selectedRowId || isSubmitting}
            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white font-bold text-xs sm:text-sm shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/45 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:shadow-none transition-all flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Spinner />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <span>{type === 'reverse' ? 'Reverse Shipment' : 'Create Shipment'}</span>
                <ArrowRight size={14} />
              </>
            )}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default ViewRateForword;